/// <amd-module name="TFS/CreateProject/Sources/ApiSource" />
import { Constants, ProjectVisibilityConstants } from "TFS/CreateProject/Constants";
import { INewProjectParameters } from "TFS/CreateProject/Contracts";
import * as CreateProjectResources from "TFS/CreateProject/Resources";
import { ProjectVisibility, TeamProject } from "Tfs/Platform/Contracts/Core";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { OperationReference } from "VSS/Platform/Contracts/Operations";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import * as VSS_Trace from "VSS/Platform/Trace";

export class ApiSource {
    private _coreRestClient: ICoreRestClient;

    constructor(private _pageContext: IVssPageContext) {}

    /**
     * Create a new project with the provided params under the given tfs context
     * @param projectParams - New project creation parameters
     */
    public createProject(projectParams: INewProjectParameters): Promise<OperationReference> {
        const teamProject: TeamProject = {
            name: projectParams.projectName,
            description: projectParams.projectDescription,
            visibility: this._getProjectVisibility(projectParams.projectVisibilityOption),
            capabilities: {
                versioncontrol: {
                    sourceControlType: projectParams.versionControlOption
                },
                processTemplate: {
                    templateTypeId: projectParams.processTemplateTypeId
                },
                features:
                    projectParams.featureIds && projectParams.featureIds.length ? { featuresEnabled: projectParams.featureIds.join(",") } : undefined
            } as { [key: string]: { [key: string]: string } }
        } as TeamProject;

        const performanceService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const scenarioName = "CreateProjectPanel.ApiSource.CreateProject";
        performanceService.startScenario(scenarioName, false);

        return this.coreRestClient.queueCreateProject(teamProject).then(
            (response: OperationReference) => {
                performanceService.endScenario(Constants.Area, scenarioName, false);
                return response;
            },
            (error: Error) => {
                performanceService.endScenario(Constants.Area, scenarioName, false, undefined, { status: "failed" });
                VSS_Trace.traceError(this._pageContext, Constants.Area, "CreateProjectPanel.ApiSource.CreateProject.Failed", error.message, {
                    error: error
                });

                throw error;
            }
        );
    }

    private _getProjectVisibility(visibility: string | null | undefined): ProjectVisibility {
        switch (visibility) {
            case ProjectVisibilityConstants.TeamMembers:
                return ProjectVisibility.Private;
            case ProjectVisibilityConstants.EveryoneInTenant:
                return ProjectVisibility.Organization;
            case ProjectVisibilityConstants.Everyone:
                return ProjectVisibility.Public;
            default:
                throw new Error(Utils_String.format(CreateProjectResources.CreateProjectInvalidVisibilityValue, visibility));
        }
    }

    private get coreRestClient(): ICoreRestClient {
        if (!this._coreRestClient) {
            this._coreRestClient = this._pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
        }

        return this._coreRestClient;
    }
}
